/**
 * Shared error handling utilities for error boundaries.
 * @module error/utils
 */

/**
 * Extracts a human-readable message from an unknown error type.
 * Handles Error objects, strings, and unknown values gracefully.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

/**
 * Type for react-error-boundary onError callback info parameter.
 */
export interface ErrorBoundaryInfo {
  componentStack?: string | null;
}

/**
 * Creates a standardized error logging function for error boundaries.
 * @param context - The component/feature context (e.g., 'DashboardGrid', 'ChartRenderer')
 * @param metadata - Additional metadata to include in the log
 */
export function createErrorHandler(context: string, metadata: Record<string, unknown> = {}) {
  return (error: unknown, info: ErrorBoundaryInfo) => {
    console.error(`[${context}] Error:`, {
      error: getErrorMessage(error),
      componentStack: info.componentStack,
      ...metadata,
    });
  };
}
